import React from 'react';
import { FormattedMessage } from 'react-intl';
import { DialogContentText, Grid, makeStyles, Typography } from '@material-ui/core';
import * as yup from 'yup';
import { FontWeightProperty } from 'csstype';
import { yupResolver } from '@hookform/resolvers/yup';

import messagesCrm from 'modules/crm/messages/redeems';
import commonMessages from 'common/messages/messages';
import {
  DialogComponent,
  PasswordField,
  PinCodeMaskedInput,
  TextArea,
  TextField,
} from 'common/components';
import messages from 'common/components/PersonProfile/messages';
import { Controller, useForm } from 'react-hook-form';
import notes from 'common/components/PersonProfile/components/Notes/messages/notes';
import inputLabels from 'common/messages/inputLabels';
import { CustomTheme } from 'common/ui/interfaces';
import { useRenderIntlMessage } from 'common/hooks/useRenderIntlMessage';
import { getRequiredErrorMessage } from 'common/utils/validation';
import inputErrors from 'common/messages/inputErrors';
import useSearchNoteCodes from 'common/httpHooks/useSearchNoteCodes';
import { INoteCodeCreate, IUnredeemDto } from 'modules/reports/interfaces/usedSessionsReport';
import { INamedEntity } from 'common/interfaces/common';
import Autocomplete from 'common/components/Autocomplete/Autocomplete';

interface IUnredeemSessionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: IUnredeemDto) => void;
  disabled: boolean;
  isBulkUnredeem: boolean;
  numSelected?: number;
}

interface INoteCodeCreateForm extends Omit<INoteCodeCreate, 'noteCodeId'> {
  noteCode: INamedEntity | null;
}

interface IUnredeemSessionsForm extends Omit<IUnredeemDto, 'note'> {
  note: INoteCodeCreateForm;
}

const useStyles = makeStyles((theme: CustomTheme) => ({
  blockTitle: {
    textTransform: 'uppercase',
    fontWeight: theme.typography.fontWeightBold as FontWeightProperty,
    color: theme.palette.text.secondary,
    opacity: 0.4,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
}));

const validationSchema = yup.object().shape({
  pinCode: yup
    .string()
    .nullable()
    .required(getRequiredErrorMessage)
    .when({
      is: val => val?.length,
      then: rule => rule.min(4, () => inputErrors.pinCodeLengthError),
    }),
});

const UnredeemSessionsModal = (props: IUnredeemSessionsModalProps): JSX.Element => {
  const { isOpen, onClose, onSubmit, disabled, isBulkUnredeem, numSelected } = props;

  const classes = useStyles();

  const renderIntlMessage = useRenderIntlMessage();

  const { isLoading, fetchNoteCodes, noteCodes, clearNoteCodes } = useSearchNoteCodes();

  const {
    formState: { errors },
    control,
    handleSubmit,
  } = useForm<IUnredeemSessionsForm>({
    defaultValues: {
      pinCode: '',
      note: {
        noteCode: null,
        text: '',
      },
    },
    resolver: yupResolver(validationSchema) as any, // TODO - PRM-1810 need resolver type
    shouldUnregister: false,
    mode: 'onTouched',
  });

  const formId = 'unredeem_sessions_form';

  const submitForm = (formData: IUnredeemSessionsForm) => {
    const {
      note: { noteCode, text },
      pinCode,
    } = formData;

    onSubmit({
      pinCode,
      note: {
        noteCodeId: noteCode && noteCode.id,
        text,
      },
    });
  };

  return (
    <DialogComponent
      title={<FormattedMessage {...commonMessages.unredeemBtn} />}
      submitBtnTitle={<FormattedMessage {...commonMessages.unredeemBtn} />}
      size="xs"
      formId={formId}
      isOpen={isOpen}
      onClose={onClose}
      disableFullScreenBehavior
      disabled={disabled}
    >
      <DialogContentText>
        <FormattedMessage
          {...(isBulkUnredeem
            ? messagesCrm.unredeemSessionsQuestionBody
            : messagesCrm.unredeemSessionQuestionBody)}
          values={{ count: numSelected }}
        />
      </DialogContentText>
      <form id={formId} onSubmit={handleSubmit(submitForm)}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography className={classes.blockTitle}>
              <FormattedMessage {...messages.redeemDescriptionTitle} />
            </Typography>

            <Controller
              name="note.text"
              control={control}
              render={({ field }) => (
                <TextArea
                  value={field.value}
                  label={<FormattedMessage {...messages.redeemNoteLabel} />}
                  variant="outlined"
                  fullWidth
                  minRows={3}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  maxSymbols={1000}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="note.noteCode"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  options={noteCodes}
                  loading={isLoading}
                  value={field.value as any} // TODO - PRM-1810 need type
                  multiple={false}
                  getOptionLabel={option => (option ? option.title : '')}
                  onChange={(e, option) => field.onChange(option)}
                  onBlur={field.onBlur}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label={<FormattedMessage {...notes.noteCode} />}
                      variant="outlined"
                      fullWidth
                      onChange={e => {
                        fetchNoteCodes(e.target.value);
                      }}
                      onFocus={e => {
                        fetchNoteCodes(e.target.value);
                      }}
                      onBlur={clearNoteCodes}
                      inputProps={{
                        ...params.inputProps,
                        autoComplete: 'off',
                      }}
                    />
                  )}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography className={classes.blockTitle}>
              <FormattedMessage {...messages.redeemPinTitle} />
            </Typography>

            <Controller
              name="pinCode"
              control={control}
              render={({ field }) => (
                <PasswordField
                  autoComplete="new-password"
                  variant="outlined"
                  label={<FormattedMessage {...inputLabels.pinCode} />}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  fullWidth
                  value={field.value}
                  InputProps={{ inputComponent: PinCodeMaskedInput }}
                  error={!!errors.pinCode}
                  helperText={renderIntlMessage(errors.pinCode?.message)}
                />
              )}
            />
          </Grid>
        </Grid>
      </form>
    </DialogComponent>
  );
};

export default UnredeemSessionsModal;
